import React, { useCallback, useEffect, useState } from 'react';
import { useTheme, ITheme } from 'src/theme';
import { styled } from '@linaria/react';
import { PaymentPremiumAmount } from '@models/MLTC/Payment.model';
import useForm from '@utils/useForm';
import { Helpers } from '@utils/helpers';

import ContainerBlock from '@components/ContainerBlock';
import InformationBox from '@components/InformationBox';
import Text from '@components/Text';
import Heading from '@components/Heading';
import Input from '@components/Input';
import Button from '@components/Button';
import Bullet from '@components/Bullet';

import { validate } from './service';
import { isEmpty } from 'lodash';

const FormContainer = styled.form<{ theme: ITheme }>`
  .payment-form {
    border: ${props => `1px solid ${props.theme.colors.grey[700]}`};
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  button {
    width: auto;
  }
`;

const PriceBlock = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`

export interface InitialPremiumFormProps {
  isEditing?: boolean;
  onSave: (value: PaymentPremiumAmount) => void;
  onCancel: (value: PaymentPremiumAmount) => void;
  premium: number;
  nric?: string;

}

function InitialPremiumForm({ isEditing, onSave, onCancel, premium, nric = '-' }: InitialPremiumFormProps) {
  const theme: ITheme = useTheme();
  const { values, errors, handleChange, patchValue, formValid } = useForm(new PaymentPremiumAmount({ cpfMediSaveAmount: premium <= 600 ? premium : 600, creditCardAmount: premium }), () => {}, validate);
  const [finalValue, setFinalValue] = useState<PaymentPremiumAmount>(new PaymentPremiumAmount({ cpfMediSaveAmount: premium <= 600 ? premium : 600, creditCardAmount: premium }));
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);

  const onChange = useCallback((event: any) => {
    const { target: { value } } = event;
    handleChange(event);
    const amount = Number(value);
    if (amount <= premium) {
      const creditCardAmount = amount > 600 ? '' : Helpers.toTwoDecimal(premium - amount);
      patchValue({ creditCardAmount });
    } else if (amount < 600) {
      const creditCardAmount = amount > 600 ? '' : Helpers.toTwoDecimal(premium - amount);
      patchValue({ creditCardAmount });
      // patchValue({
      //   cpfMediSaveAmount: Number(premium) <= 600 ? Number(premium) : Number(premium) - Number(values.cpfMediSaveAmount),
      //   creditCardAmount: Helpers.toTwoDecimal(Number(premium) <= 600 ? 0 : Number(premium) - Number(values.cpfMediSaveAmount))
      // }); 
    } else {
      patchValue({ creditCardAmount: '' });
    }


  }, [handleChange, premium, patchValue]);

  const handleOnSave = useCallback(() => {
    const final = {
      cpfMediSaveAmount: Number(values.cpfMediSaveAmount),
      creditCardAmount: Number(values.creditCardAmount),
    }
    setFinalValue(final);
    onSave(final);
    //onCancel()
  }, [onSave, values, onCancel]);

  const handleCancel = useCallback(() => {
    console.log(
      'QnB ➤ useEffect() ➤ handleCancel',
      {
        'local ➤ values': values,
        premium
      }
    );
    const final = {
      ...finalValue,
      creditCardAmount: Number(Helpers.toTwoDecimal(Number(premium) <= 600 ? 0 : Number(premium) - Number(values.cpfMediSaveAmount)))
    }
    setFinalValue(final);
    onSave(final);
    //onCancel();
  }, [onCancel, finalValue, onSave, premium]);

  useEffect(() => {
    console.log(
      'QnB ➤ useEffect() ➤ InitialPremiumForm',
      {
        'local ➤ values': values,
        premium
      }
    );
    // handleChange({ target: { name: 'cpfMediSaveAmount', value: values?.cpfMediSaveAmount } });
    if (!isFirstLoad && premium != undefined) {
      setIsFirstLoad(true);
      patchValue({
        cpfMediSaveAmount: Number(premium) <= 600 ? Helpers.toTwoDecimal(Number(premium)) : 600,
        creditCardAmount: Helpers.toTwoDecimal(Number(premium) <= 600 ? 0 : Number(premium) - Number(values.cpfMediSaveAmount))
      });      
    }

  }, [patchValue, premium]);

  return (
    <>
      {(isEditing) ? (
        <FormContainer theme={theme} onSubmit={(e) => e.preventDefault()} noValidate>
          <ContainerBlock className='payment-form' padding="30px" paddingTablet="30px" margin="0 0 35px" border={`1px solid ${theme.colors.grey[700]}`}>
            <InformationBox hasClose margin="0 0 30px">
              <Text>To pay full amount using credit card, please indicate &quot;0&quot; under CPF MediSave amount.</Text>
            </InformationBox>
            <Heading variant="label" margin="0 0 10px">CPF MediSave amount</Heading>
            <Text margin="0 0 15px">Maximum CPF MediSave deduction limit is S$600 </Text>
            {!errors?.cpfMediSaveAmount?.isValid && errors?.cpfMediSaveAmount?.message && <Bullet variant="error">{errors?.cpfMediSaveAmount?.message}</Bullet>}
            <Input boxLabel="S$" margin="0 0 30px" type="decimal" max={String(premium)} maxLength={10} variant="prefix" name='cpfMediSaveAmount' value={values['cpfMediSaveAmount']} onChange={onChange} invalid={!isEmpty(errors?.cpfMediSaveAmount?.message)} />
            <Heading variant="label" margin="0 0 10px">Credit card amount</Heading>
            <Text margin="0 0 15px">Remaining amount after CPF MediSave deduction</Text>
            <Input boxLabel="S$" readOnly margin="0 0 30px" variant="prefix"  name='creditCardAmount' value={values['creditCardAmount']} />
            <ButtonsContainer>
              <Button label='Save' margin="0 20px 0 0 " onClick={handleOnSave} disabled={!formValid} />
              <Button label='Cancel' margin="0" variant='secondary' onClick={handleCancel} />
            </ButtonsContainer>
          </ContainerBlock>
        </FormContainer>
      ) : (
        <>
          <Text margin="0 0 30px" fontWeight={300}>The payment mode for the initial premium will apply to subsequent premiums.</Text>
          <PriceBlock>
            <div>
              <Text margin={0} fontWeight={600}>CPF MediSave amount</Text>
              <Text margin={0} fontWeight={300}>Account number: {nric}</Text>
            </div>
            <Heading margin={0} variant='sub' fontWeight={300}>{Helpers.toCurrency(finalValue.cpfMediSaveAmount)}</Heading>
          </PriceBlock>
          <PriceBlock>
            <div>
              <Text margin={0} fontWeight={600}>Credit card</Text>
              <Text margin={0} fontWeight={300}>(Remaining amount after CPF MediSave deduction)</Text>
            </div>
            <Heading margin={0} variant='sub' fontWeight={300}>{Helpers.toCurrency(finalValue.creditCardAmount)}</Heading>
          </PriceBlock>
        </>
      )}
    </>

  );
};

export default InitialPremiumForm;
